import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    view: state.pages.productList.view,
    productsDataView: getProductListDataView(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface NISProductListColumnsStyles {
    container?: GridContainerProps;
    noSearchContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    hiddenLeftColumnStyles?: HiddenProps;
    hiddenProductListStyles?: HiddenProps;
    nosearchLeftGridStyles?: GridItemProps;
    nosearchRightGridStyles?: GridItemProps;
    spinner: LoadingSpinnerProps;
}

export const niscolumnsStyles: NISProductListColumnsStyles = {
    spinner: {
        css: css`
            margin: auto;
            display: block;
            width: 80%;
        `,
    },
    container: {
        gap: 20,
        css: css`
            padding: 0px 53px !important;
            @media (min-width: 992px) and (max-width: 1199px) {
                padding: 0px 40px !important;
            }

            > div[class^="GridOffset"] {
                margin: 0px !important;
                width: 100% !important;
                gap: 20px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px 20px !important;
                        padding-top: 24px !important;
                    `,
                    css`
                        padding: 0px 20px !important;
                        padding-top: 24px !important;
                    `,
                    css`
                        padding: 0px 30px !important;
                        padding-top: 24px !important;
                    `,
                    css`
                        padding-top: 54px !important;
                    `,
                    css`
                        padding-top: 54px !important;
                    `,
                ])}
        `,
    },
    noSearchContainer: {
        css: css`
            max-width: fit-content !important;
            margin: 64px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 25px;
                        margin-bottom: 25px;
                        margin-left: 20px;
                        margin-right: 20px;
                    `,
                    css`
                        margin-top: 25px;
                        margin-bottom: 25px;
                        margin-left: 20px;
                        margin-right: 20px;
                    `,
                    css`
                        margin-top: 45px;
                        margin-bottom: 45px;
                        margin-left: 45px;
                        margin-right: 45px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-basis: 23.57% !important;
            max-width: 23.57% !important;

            @media (min-width: 992px) and (max-width: 1199px) {
                flex-basis: 22.57% !important;
                max-width: 22.57% !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 0, 0],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
        css: css`
            flex-basis: 74.5% !important;
            max-width: 74.5% !important;
            @media (min-width: 992px) and (max-width: 1199px) {
                flex-basis: 75.2% !important;
                max-width: 75.2% !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                    `,
                    css`
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                    `,
                    css`
                        flex-basis: 100% !important;
                        max-width: 100% !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    rightColumnTableViewGridItem: {
        width: 12,
    },
    hiddenLeftColumnStyles: {
        css: css`
            width: 100%;
        `,
    },
    hiddenProductListStyles: {
        css: css`
            width: 100%;
        `,
    },
    nosearchRightGridStyles: {
        width: [12, 12, 6, 6, 6],
    },
    nosearchLeftGridStyles: {
        width: [12, 12, 6, 6, 6],
        css: css`
            margin-top: 35px;
        `,
    },
};

const styles = niscolumnsStyles;

const NISProductListColumns: FC<Props> = ({ id, view, productsDataView }) => {
    const products = productsDataView.value;
    if (!products) {
        return <LoadingSpinner {...styles.spinner} />;
    }

    const leftColumnGridItemStyle = view === "Table" ? styles.leftColumnTableViewGridItem : styles.leftColumnGridItem;
    const rightColumnGridItemStyle =
        view === "Table" ? styles.rightColumnTableViewGridItem : styles.rightColumnGridItem;

    return products?.length > 0 ? (
        <GridContainer {...styles.container}>
            <GridItem {...leftColumnGridItemStyle}>
                <Hidden {...styles.hiddenLeftColumnStyles} below="lg">
                    <Zone contentId={id} zoneName="Content00" />
                </Hidden>
                <Hidden above="sm" {...styles.hiddenProductListStyles}>
                    <Zone contentId={id} zoneName="Content02" />
                </Hidden>
            </GridItem>
            <GridItem {...rightColumnGridItemStyle}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
        </GridContainer>
    ) : (
        <GridContainer {...styles.noSearchContainer}>
            <GridItem {...styles.nosearchLeftGridStyles}>
                <Zone contentId={id} zoneName="Content05" />
            </GridItem>
            <GridItem {...styles.nosearchRightGridStyles}>
                <Zone contentId={id} zoneName="Content06" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(NISProductListColumns),
    definition: {
        group: "Product List",
        displayName: "NIS Columns",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
